/**
 * Copyright 2023 Google LLC
 * Licensed under the Apache License, Version 2.0
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Adapted from Google Maps Platform Neighborhood Discovery and Commutes
 * widgets for the Rathna Stores location pages.
 */
(function () {
  'use strict';

  var ND_NUM_PLACES_INITIAL = 5;
  var ND_NUM_PLACES_SHOW_MORE = 5;
  var ND_NUM_PLACE_PHOTOS_MAX = 6;
  var ND_DEFAULT_POI_MIN_ZOOM = 18;
  var MAX_NUM_DESTINATIONS = 10;
  var BIAS_BOUND_DISTANCE = 0.5;
  var HOUR_IN_SECONDS = 3600;
  var MIN_IN_SECONDS = 60;

  var STROKE_COLORS = {
    active: { innerStroke: '#F03D41', outerStroke: '#D6282D' },
    inactive: { innerStroke: '#BDC1C6', outerStroke: '#80868B' }
  };
  var MARKER_ICON_COLORS = {
    active: { fill: '#F03D41', stroke: '#D6282D', label: '#FFF' },
    inactive: { fill: '#F1F3F4', stroke: '#9AA0A6', label: '#3C4043' }
  };
  var DestinationOperation = { ADD: 'ADD', EDIT: 'EDIT', DELETE: 'DELETE' };
  var TravelMode = {
    DRIVING: 'DRIVING',
    TRANSIT: 'TRANSIT',
    BICYCLING: 'BICYCLING',
    WALKING: 'WALKING'
  };

  function hideElement(el, focusEl) {
    if (!el) return;
    el.style.display = 'none';
    if (focusEl) focusEl.focus();
  }

  function showElement(el, focusEl, display) {
    if (!el) return;
    el.style.display = display || 'block';
    if (focusEl) focusEl.focus();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function parsePlaceIds(value) {
    if (Array.isArray(value)) {
      return value.map(function (item) { return String(item).trim(); }).filter(Boolean);
    }
    return String(value || '')
      .split(/[\s,]+/)
      .map(function (item) { return item.trim(); })
      .filter(Boolean);
  }

  function parseLatLng(center) {
    var lat = parseFloat(center && center.lat);
    var lng = parseFloat(center && center.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat: lat, lng: lng };
  }

  function loadGoogleMaps(apiKey) {
    if (window.google && window.google.maps && window.google.maps.places) {
      return Promise.resolve();
    }
    if (!apiKey) return Promise.reject(new Error('Missing Google Maps API key'));
    if (!window.__storeMapsScriptPromise) {
      window.__storeMapsScriptPromise = new Promise(function (resolve, reject) {
        var existing = document.querySelector('script[data-store-maps-google="1"], script[data-store-selector-google-maps="1"]');
        if (existing) {
          if (window.google && window.google.maps && window.google.maps.places) {
            resolve();
            return;
          }
          existing.addEventListener('load', function () { resolve(); }, { once: true });
          existing.addEventListener('error', function () { reject(new Error('Google Maps failed to load')); }, { once: true });
          return;
        }
        var script = document.createElement('script');
        script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&libraries=places&v=weekly';
        script.async = true;
        script.defer = true;
        script.dataset.storeMapsGoogle = '1';
        script.onload = function () { resolve(); };
        script.onerror = function () { reject(new Error('Google Maps failed to load')); };
        document.head.appendChild(script);
      });
    }
    return window.__storeMapsScriptPromise;
  }

  function formatPlaceType(str) {
    if (!str) return '';
    return (str.charAt(0).toUpperCase() + str.slice(1)).replace(/_/g, ' ');
  }

  function parseDaysHours(openingHours) {
    if (!openingHours || !openingHours.weekday_text) return [];
    var daysHours = openingHours.weekday_text.map(function (entry) {
      var parts = entry.split(/:\s+/);
      return { days: parts[0].substr(0, 3), hours: parts[1] };
    });
    for (var i = 1; i < daysHours.length; i++) {
      if (daysHours[i - 1].hours === daysHours[i].hours) {
        if (daysHours[i - 1].days.indexOf('-') !== -1) {
          daysHours[i - 1].days = daysHours[i - 1].days.replace(/\w+$/, daysHours[i].days);
        } else {
          daysHours[i - 1].days += ' - ' + daysHours[i].days;
        }
        daysHours.splice(i--, 1);
      }
    }
    return daysHours;
  }

  function hasHiddenContent(el) {
    var noscroll = window.getComputedStyle(el).overflowY.indexOf('hidden') !== -1;
    return noscroll && el.scrollHeight > el.clientHeight;
  }

  function fillDistanceCards(root, center, strings) {
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-distance-card]'));
    if (!cards.length) return Promise.resolve();
    var destinations = cards.map(function (card) { return card.getAttribute('data-query'); });
    var service = new google.maps.DistanceMatrixService();
    var walkSuffix = (strings && strings.walk_suffix) || ' walk';
    var driveSuffix = (strings && strings.drive_suffix) || ' drive';

    function apply(elements, suffix) {
      elements.forEach(function (trip, index) {
        if (!trip || trip.status !== 'OK') return;
        var card = cards[index];
        if (card.dataset.live === '1') return;
        var distanceEl = card.querySelector('[data-distance]');
        var durationEl = card.querySelector('[data-duration]');
        var linkEl = card.querySelector('[data-directions-link]');
        if (distanceEl && trip.distance) distanceEl.textContent = trip.distance.text;
        if (durationEl && trip.duration) durationEl.textContent = trip.duration.text + (suffix || '');
        if (linkEl && center) {
          linkEl.href = 'https://www.google.com/maps/dir/?api=1&origin=' + center.lat + ',' + center.lng +
            '&destination=' + encodeURIComponent(destinations[index]) + '&travelmode=walking';
        }
        card.dataset.live = '1';
      });
    }

    return new Promise(function (resolve) {
      service.getDistanceMatrix({
        origins: [center],
        destinations: destinations,
        travelMode: google.maps.TravelMode.WALKING,
        unitSystem: google.maps.UnitSystem.METRIC
      }, function (result, status) {
        if (status === 'OK' && result && result.rows[0]) {
          var elements = result.rows[0].elements || [];
          var missing = elements.some(function (trip) { return !trip || trip.status !== 'OK'; });
          apply(elements, walkSuffix);
          if (!missing) {
            resolve();
            return;
          }
        }
        service.getDistanceMatrix({
          origins: [center],
          destinations: destinations,
          travelMode: google.maps.TravelMode.DRIVING,
          unitSystem: google.maps.UnitSystem.METRIC
        }, function (driveResult, driveStatus) {
          if (driveStatus === 'OK' && driveResult && driveResult.rows[0]) {
            apply(driveResult.rows[0].elements || [], driveSuffix);
          }
          resolve();
        });
      });
    });
  }

  function findPlaceFromQuery(service, query) {
    return new Promise(function (resolve) {
      service.findPlaceFromQuery({
        query: query,
        fields: ['place_id', 'name', 'geometry']
      }, function (results, status) {
        if (status === google.maps.places.PlacesServiceStatus.OK && results && results[0]) {
          resolve(results[0]);
        } else {
          resolve(null);
        }
      });
    });
  }

  function renderPlaceResults(places) {
    return places.map(function (place) {
      var photo = place.photos && place.photos[0] ? place.photos[0].urlSmall : '';
      return (
        '<li class="place-result">' +
          '<div class="text">' +
            '<button type="button" class="name">' + escapeHtml(place.name || '') + '</button>' +
            '<div class="info">' + escapeHtml(place.type || '') + '</div>' +
          '</div>' +
          '<button type="button" class="photo" style="background-image:url(' + escapeHtml(photo) + ')" aria-label="show photo"></button>' +
        '</li>'
      );
    }).join('');
  }

  function renderPlaceDetails(place, strings) {
    var html = '<div class="navbar"><button type="button" class="back-button">' +
      '<span class="icon" aria-hidden="true">←</span> ' + escapeHtml(strings.back) +
      '</button></div><header><h3>' + escapeHtml(place.name || '') + '</h3>';
    if (place.url) {
      html += '<div class="info"><a href="' + escapeHtml(place.url) + '" target="_blank" rel="noopener noreferrer">' +
        escapeHtml(strings.see_on_maps) + '</a></div>';
    }
    if (place.type) html += '<div class="info">' + escapeHtml(place.type) + '</div>';
    if (place.duration && place.duration.text) {
      html += '<div class="info"><span>' + escapeHtml(place.duration.text) + ' drive</span></div>';
    }
    html += '</header><div class="section">';
    if (place.address) {
      html += '<div class="contact"><div class="text">' + escapeHtml(place.address) + '</div></div>';
    }
    if (place.website) {
      html += '<div class="contact"><div class="text"><a href="' + escapeHtml(place.website) +
        '" target="_blank" rel="noopener noreferrer">' + escapeHtml(place.websiteDomain || place.website) +
        '</a></div></div>';
    }
    if (place.phoneNumber) {
      html += '<div class="contact"><div class="text"><a href="tel:' + escapeHtml(place.phoneNumber) + '">' +
        escapeHtml(place.phoneNumber) + '</a></div></div>';
    }
    if (place.openingHours && place.openingHours.length) {
      html += '<div class="contact"><div class="text">';
      place.openingHours.forEach(function (row) {
        html += '<div><span class="weekday">' + escapeHtml(row.days) + '</span> <span class="hours">' +
          escapeHtml(row.hours) + '</span></div>';
      });
      html += '</div></div>';
    }
    html += '</div>';
    if (place.photos && place.photos.length) {
      html += '<div class="photos section">';
      place.photos.forEach(function (photo, index) {
        html += '<button type="button" class="photo" data-photo-index="' + index +
          '" style="background-image:url(' + escapeHtml(photo.urlLarge) + ')" aria-label="' +
          escapeHtml(strings.show_photo) + '"></button>';
      });
      html += '</div>';
    }
    return html;
  }

  function NeighborhoodDiscovery(widgetEl, configuration) {
    var widget = this;
    widget.center = configuration.mapOptions.center;
    widget.places = configuration.pois || [];

    initializeMap();
    initializePlaceDetails();
    initializeSidePanel();
    initializeSearchInput();
    initializeDistanceMatrix();
    initializeDirections();

    function initializeMap() {
      var mapOptions = Object.assign({}, configuration.mapOptions);
      widget.mapBounds = new google.maps.Circle({
        center: widget.center,
        radius: configuration.mapRadius
      }).getBounds();
      mapOptions.restriction = { latLngBounds: widget.mapBounds };
      mapOptions.mapTypeControlOptions = { position: google.maps.ControlPosition.TOP_RIGHT };
      widget.map = new google.maps.Map(widgetEl.querySelector('.map'), mapOptions);
      widget.map.fitBounds(widget.mapBounds, 0);
      widget.map.addListener('click', function (event) {
        if (event.placeId) {
          event.stop();
          widget.selectPlaceById(event.placeId);
        }
      });
      widget.map.addListener('zoom_changed', function () {
        var hideDefaultPoiPins = widget.map.getZoom() < ND_DEFAULT_POI_MIN_ZOOM;
        widget.map.setOptions({
          styles: [{
            featureType: 'poi',
            elementType: hideDefaultPoiPins ? 'labels' : 'labels.text',
            stylers: [{ visibility: 'off' }]
          }]
        });
      });

      var markerPath = widgetEl.querySelector('.marker-pin path').getAttribute('d');
      var drawMarker = function (title, position, fillColor, strokeColor, labelText) {
        return new google.maps.Marker({
          title: title,
          position: position,
          map: widget.map,
          icon: {
            path: markerPath,
            fillColor: fillColor,
            fillOpacity: 1,
            strokeColor: strokeColor,
            anchor: new google.maps.Point(13, 35),
            labelOrigin: new google.maps.Point(13, 13)
          },
          label: {
            text: labelText,
            color: 'white',
            fontSize: '14px'
          }
        });
      };

      drawMarker('Rathna Stores', widget.center, '#663091', '#2E1242', '●');

      widget.addPlaceMarker = function (place) {
        place.marker = drawMarker(place.name, place.coords, '#F03D41', '#D6282D', '●');
        place.marker.addListener('click', function () {
          widget.selectPlaceById(place.placeId);
        });
      };

      widget.updateBounds = function (places) {
        var bounds = new google.maps.LatLngBounds();
        bounds.extend(widget.center);
        places.forEach(function (place) {
          if (place.marker) bounds.extend(place.marker.getPosition());
        });
        widget.map.fitBounds(bounds, 100);
      };

      widget.selectedPlaceMarker = new google.maps.Marker({ title: 'Point of Interest' });
      widget.resize = function () {
        google.maps.event.trigger(widget.map, 'resize');
        if (widget.mapBounds) widget.map.fitBounds(widget.mapBounds, 0);
      };
    }

    function initializePlaceDetails() {
      var detailsService = new google.maps.places.PlacesService(widget.map);
      var placeIdsToDetails = new Map();
      widget.places.forEach(function (place) {
        placeIdsToDetails.set(place.placeId, place);
        place.fetchedFields = new Set(['place_id']);
      });

      widget.fetchPlaceDetails = function (placeId, fields, callback) {
        if (!placeId || !fields) return;
        var place = placeIdsToDetails.get(placeId);
        if (!place) {
          place = { placeId: placeId, fetchedFields: new Set(['place_id']) };
          placeIdsToDetails.set(placeId, place);
        }
        var missingFields = fields.filter(function (field) { return !place.fetchedFields.has(field); });
        if (missingFields.length === 0) {
          callback(place);
          return;
        }
        var request = { placeId: placeId, fields: missingFields };
        var retryCount = 0;
        var processResult = function (result, status) {
          if (status !== google.maps.places.PlacesServiceStatus.OK) {
            if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT && retryCount < 5) {
              var delay = (Math.pow(2, retryCount) + Math.random()) * 500;
              setTimeout(function () { detailsService.getDetails(request, processResult); }, delay);
              retryCount += 1;
            }
            return;
          }
          if (result.name) place.name = result.name;
          if (result.geometry) place.coords = result.geometry.location;
          if (result.formatted_address) place.address = result.formatted_address;
          if (result.photos) {
            place.photos = result.photos.map(function (photo) {
              return {
                urlSmall: photo.getUrl({ maxWidth: 200, maxHeight: 200 }),
                urlLarge: photo.getUrl({ maxWidth: 1200, maxHeight: 1200 }),
                attrs: photo.html_attributions
              };
            }).slice(0, ND_NUM_PLACE_PHOTOS_MAX);
          }
          if (result.types) {
            place.type = formatPlaceType(result.types[0]);
            for (var i = 0; i < result.types.length; i++) {
              if (result.types[i] === 'restaurant' || result.types[i] === 'cafe' || result.types[i].indexOf('store') !== -1) {
                place.type = formatPlaceType(result.types[i]);
                break;
              }
            }
          }
          if (result.url) place.url = result.url;
          if (result.website) {
            place.website = result.website;
            try {
              place.websiteDomain = new URL(place.website).hostname;
            } catch (error) {
              place.websiteDomain = place.website;
            }
          }
          if (result.formatted_phone_number) place.phoneNumber = result.formatted_phone_number;
          if (result.opening_hours) place.openingHours = parseDaysHours(result.opening_hours);
          missingFields.forEach(function (field) { place.fetchedFields.add(field); });
          callback(place);
        };

        if (widget.placeIdsToAutocompleteResults) {
          var autoCompleteResult = widget.placeIdsToAutocompleteResults.get(placeId);
          if (autoCompleteResult) {
            processResult(autoCompleteResult, google.maps.places.PlacesServiceStatus.OK);
            return;
          }
        }
        detailsService.getDetails(request, processResult);
      };
    }

    function initializeSidePanel() {
      var placesPanelEl = widgetEl.querySelector('.places-panel');
      var detailsPanelEl = widgetEl.querySelector('.details-panel');
      var placeResultsEl = widgetEl.querySelector('.place-results-list');
      var showMoreButtonEl = widgetEl.querySelector('.show-more-button');
      var photoModalEl = widgetEl.querySelector('.photo-modal');
      var strings = configuration.strings || {};

      var showPhotoModal = function (photo, placeName) {
        var prevFocusEl = document.activeElement;
        var imgEl = photoModalEl.querySelector('img');
        imgEl.src = photo.urlLarge;
        var backButtonEl = photoModalEl.querySelector('.back-button');
        var onBack = function () {
          hideElement(photoModalEl, prevFocusEl);
          imgEl.src = '';
          backButtonEl.removeEventListener('click', onBack);
        };
        backButtonEl.addEventListener('click', onBack);
        photoModalEl.querySelector('.photo-place').textContent = placeName;
        photoModalEl.querySelector('.photo-attrs span').innerHTML = photo.attrs || '';
        var attributionEl = photoModalEl.querySelector('.photo-attrs a');
        if (attributionEl) attributionEl.setAttribute('target', '_blank');
        showElement(photoModalEl, backButtonEl);
      };

      var selectedPlaceId;
      widget.selectPlaceById = function (placeId, panToMarker) {
        if (selectedPlaceId === placeId) return;
        selectedPlaceId = placeId;
        var prevFocusEl = document.activeElement;
        var showDetailsPanel = function (place) {
          detailsPanelEl.innerHTML = renderPlaceDetails(place, strings);
          var backButtonEl = detailsPanelEl.querySelector('.back-button');
          backButtonEl.addEventListener('click', function () {
            hideElement(detailsPanelEl, prevFocusEl);
            selectedPlaceId = undefined;
            widget.updateDirections();
            widget.selectedPlaceMarker.setMap(null);
          });
          detailsPanelEl.querySelectorAll('.photo').forEach(function (photoEl, index) {
            photoEl.addEventListener('click', function () {
              showPhotoModal(place.photos[index], place.name);
            });
          });
          showElement(detailsPanelEl, backButtonEl);
          detailsPanelEl.scrollTop = 0;
        };
        var processResult = function (place) {
          if (place.marker) {
            widget.selectedPlaceMarker.setMap(null);
          } else {
            widget.selectedPlaceMarker.setPosition(place.coords);
            widget.selectedPlaceMarker.setMap(widget.map);
          }
          if (panToMarker) widget.map.panTo(place.coords);
          showDetailsPanel(place);
          widget.fetchDuration(place, showDetailsPanel);
          widget.updateDirections(place);
        };
        widget.fetchPlaceDetails(placeId, [
          'name', 'types', 'geometry.location', 'formatted_address', 'photo', 'url',
          'website', 'formatted_phone_number', 'opening_hours'
        ], processResult);
      };

      var renderPlaces = function (places, startIndex) {
        placeResultsEl.insertAdjacentHTML('beforeend', renderPlaceResults(places));
        placeResultsEl.querySelectorAll('.place-result').forEach(function (resultEl, index) {
          var place = places[index - startIndex];
          if (!place) return;
          resultEl.addEventListener('click', function () {
            widget.selectPlaceById(place.placeId, true);
          });
          resultEl.querySelector('.name').addEventListener('click', function (event) {
            widget.selectPlaceById(place.placeId, true);
            event.stopPropagation();
          });
          resultEl.querySelector('.photo').addEventListener('click', function (event) {
            if (place.photos && place.photos[0]) showPhotoModal(place.photos[0], place.name);
            event.stopPropagation();
          });
          widget.addPlaceMarker(place);
        });
      };

      var nextPlaceIndex = 0;
      var showNextPlaces = function (count) {
        var nextPlaces = widget.places.slice(nextPlaceIndex, nextPlaceIndex + count);
        if (nextPlaces.length < 1) {
          hideElement(showMoreButtonEl);
          return;
        }
        showMoreButtonEl.disabled = true;
        var pending = nextPlaces.length;
        nextPlaces.forEach(function (place) {
          widget.fetchPlaceDetails(place.placeId, ['name', 'types', 'geometry.location', 'photo'], function () {
            pending -= 1;
            if (pending > 0) return;
            renderPlaces(nextPlaces, nextPlaceIndex);
            nextPlaceIndex += count;
            widget.updateBounds(widget.places.slice(0, nextPlaceIndex));
            if (nextPlaceIndex < widget.places.length || hasHiddenContent(placesPanelEl)) {
              showElement(showMoreButtonEl);
              showMoreButtonEl.disabled = false;
            } else {
              hideElement(showMoreButtonEl);
            }
          });
        });
      };

      showNextPlaces(ND_NUM_PLACES_INITIAL);
      showMoreButtonEl.addEventListener('click', function () {
        placesPanelEl.classList.remove('no-scroll');
        showMoreButtonEl.classList.remove('sticky');
        showNextPlaces(ND_NUM_PLACES_SHOW_MORE);
      });
    }

    function initializeSearchInput() {
      var searchInputEl = widgetEl.querySelector('.place-search-input');
      widget.placeIdsToAutocompleteResults = new Map();
      var autocomplete = new google.maps.places.Autocomplete(searchInputEl, {
        types: ['establishment'],
        fields: [
          'place_id', 'name', 'types', 'geometry.location', 'formatted_address', 'photo', 'url',
          'website', 'formatted_phone_number', 'opening_hours'
        ],
        bounds: widget.mapBounds,
        strictBounds: true
      });
      autocomplete.addListener('place_changed', function () {
        var place = autocomplete.getPlace();
        if (!place || !place.place_id) return;
        widget.placeIdsToAutocompleteResults.set(place.place_id, place);
        widget.selectPlaceById(place.place_id, true);
        searchInputEl.value = '';
      });
    }

    function initializeDistanceMatrix() {
      var distanceMatrixService = new google.maps.DistanceMatrixService();
      widget.fetchDuration = function (place, callback) {
        if (!widget.center || !place || !place.coords || place.duration) return;
        distanceMatrixService.getDistanceMatrix({
          origins: [widget.center],
          destinations: [place.coords],
          travelMode: google.maps.TravelMode.DRIVING
        }, function (result, status) {
          if (status === google.maps.DistanceMatrixStatus.OK) {
            var trip = result.rows[0].elements[0];
            if (trip.status === google.maps.DistanceMatrixElementStatus.OK) {
              place.duration = trip.duration;
              callback(place);
            }
          }
        });
      };
    }

    function initializeDirections() {
      var directionsService = new google.maps.DirectionsService();
      var directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true
      });
      widget.updateDirections = function (place) {
        if (!widget.center || !place || !place.coords) {
          directionsRenderer.setMap(null);
          return;
        }
        if (place.directions) {
          directionsRenderer.setMap(widget.map);
          directionsRenderer.setDirections(place.directions);
          return;
        }
        directionsService.route({
          origin: widget.center,
          destination: place.coords,
          travelMode: google.maps.TravelMode.DRIVING
        }, function (result, status) {
          if (status === google.maps.DirectionsStatus.OK) {
            place.directions = result;
            directionsRenderer.setMap(widget.map);
            directionsRenderer.setDirections(result);
          }
        });
      };
    }
  }

  function Commutes(root, configuration) {
    var commutesEl = {
      map: root.querySelector('.map-view'),
      initialStatePanel: root.querySelector('.commutes-initial-state'),
      destinationPanel: root.querySelector('.commutes-destinations'),
      modal: root.querySelector('.commutes-modal-container')
    };
    var destinationPanelEl = {
      addButton: commutesEl.destinationPanel.querySelector('.add-button'),
      container: commutesEl.destinationPanel.querySelector('.destinations-container'),
      list: commutesEl.destinationPanel.querySelector('.destination-list'),
      scrollLeftButton: commutesEl.destinationPanel.querySelector('.left-control'),
      scrollRightButton: commutesEl.destinationPanel.querySelector('.right-control'),
      getActiveDestination: function () {
        return commutesEl.destinationPanel.querySelector('.destination.active');
      }
    };
    var destinationModalEl = {
      title: commutesEl.modal.querySelector('h3'),
      form: commutesEl.modal.querySelector('[data-destination-form]'),
      destinationInput: commutesEl.modal.querySelector('input[name="destination-address"]'),
      errorMessage: commutesEl.modal.querySelector('.error-message'),
      addButton: commutesEl.modal.querySelector('.add-destination-button'),
      deleteButton: commutesEl.modal.querySelector('.delete-destination-button'),
      editButton: commutesEl.modal.querySelector('.edit-destination-button'),
      cancelButton: commutesEl.modal.querySelector('.cancel-button'),
      getTravelModeInput: function () {
        return commutesEl.modal.querySelector('input[name="travel-mode"]:checked');
      }
    };

    var commutesMap;
    var activeDestinationIndex;
    var origin = configuration.mapOptions.center;
    var destinations = configuration.destination || [];
    var markerIndex = 0;
    var lastActiveEl;
    var iconSuffix = configuration.iconSuffix || '';
    var strings = configuration.strings || {};

    var markerIconConfig = {
      path: 'M10 27c-.2 0-.2 0-.5-1-.3-.8-.7-2-1.6-3.5-1-1.5-2-2.7-3-3.8-2.2-2.8-3.9-5-3.9-8.8C1 4.9 5 1 10 1s9 4 9 8.9c0 3.9-1.8 6-4 8.8-1 1.2-1.9 2.4-2.8 3.8-1 1.5-1.4 2.7-1.6 3.5-.3 1-.4 1-.6 1Z',
      fillOpacity: 1,
      strokeWeight: 1,
      anchor: new google.maps.Point(15, 29),
      scale: 1.2,
      labelOrigin: new google.maps.Point(10, 9)
    };
    var originMarkerIcon = Object.assign({}, markerIconConfig, {
      fillColor: MARKER_ICON_COLORS.active.fill,
      strokeColor: MARKER_ICON_COLORS.active.stroke
    });
    var destinationMarkerIcon = Object.assign({}, markerIconConfig, {
      fillColor: MARKER_ICON_COLORS.inactive.fill,
      strokeColor: MARKER_ICON_COLORS.inactive.stroke
    });
    var bikeLayer = new google.maps.BicyclingLayer();
    var publicTransitLayer = new google.maps.TransitLayer();

    initMapView();
    initDestinations();
    initCommutesPanel();
    initCommutesModal();

    function parseTravelModeEnum(travelModeString) {
      switch (travelModeString) {
        case 'DRIVING': return TravelMode.DRIVING;
        case 'BICYCLING': return TravelMode.BICYCLING;
        case 'PUBLIC_TRANSIT':
        case 'TRANSIT': return TravelMode.TRANSIT;
        case 'WALKING': return TravelMode.WALKING;
        default: return null;
      }
    }

    function setTravelModeChecked(travelModeEnum) {
      var input = commutesEl.modal.querySelector('input[name="travel-mode"][value="' + travelModeEnum + '"]');
      if (input) input.checked = true;
    }

    function initMapView() {
      commutesMap = new google.maps.Map(commutesEl.map, configuration.mapOptions);
      configuration.defaultTravelModeEnum = parseTravelModeEnum(configuration.defaultTravelMode);
      setTravelModeLayer(configuration.defaultTravelModeEnum);
      createMarker(origin);
    }

    this.resize = function () {
      if (!commutesMap) return;
      google.maps.event.trigger(commutesMap, 'resize');
      if (activeDestinationIndex !== undefined && destinations[activeDestinationIndex] && destinations[activeDestinationIndex].bounds) {
        commutesMap.fitBounds(destinations[activeDestinationIndex].bounds);
      }
    };

    function initDestinations() {
      if (!configuration.initialDestinations || !configuration.initialDestinations.length) return;
      var callbackCounter = 0;
      var placesService = new google.maps.places.PlacesService(commutesMap);
      configuration.initialDestinations.forEach(function (destination) {
        destination.travelModeEnum = parseTravelModeEnum(destination.travelMode);
        var label = getNextMarkerLabel();
        placesService.getDetails({
          placeId: destination.placeId,
          fields: ['place_id', 'geometry', 'name']
        }, function (place) {
          if (!place || !place.geometry || !place.geometry.location) return;
          var travelModeEnum = destination.travelModeEnum || configuration.defaultTravelModeEnum;
          var destinationConfig = createDestinationConfig(place, travelModeEnum, label);
          getDirections(destinationConfig).then(function (response) {
            if (!response) return;
            destinations.push(destinationConfig);
            getCommutesInfo(response, destinationConfig);
            callbackCounter += 1;
            if (callbackCounter === configuration.initialDestinations.length) {
              destinations.sort(function (a, b) { return a.label < b.label ? -1 : 1; });
              var bounds = new google.maps.LatLngBounds();
              destinations.forEach(function (item, index) {
                assignMapObjectListeners(item, index);
                updateCommutesPanel(item, index, DestinationOperation.ADD);
                bounds.union(item.bounds);
              });
              var lastIndex = destinations.length - 1;
              handleRouteClick(destinations[lastIndex], lastIndex);
              commutesMap.fitBounds(bounds);
            }
          });
        });
      });
    }

    function initCommutesPanel() {
      root.querySelectorAll('.add-button').forEach(function (addButton) {
        addButton.addEventListener('click', function () {
          destinationModalEl.title.textContent = strings.add_destination || 'Add destination';
          hideElement(destinationModalEl.deleteButton);
          hideElement(destinationModalEl.editButton);
          showElement(destinationModalEl.addButton, null, 'inline');
          showModal();
          setTravelModeChecked(configuration.defaultTravelModeEnum || TravelMode.WALKING);
        });
      });
      destinationPanelEl.scrollLeftButton.addEventListener('click', handleScrollButtonClick);
      destinationPanelEl.scrollRightButton.addEventListener('click', handleScrollButtonClick);
    }

    function initCommutesModal() {
      var destinationToAdd = null;
      var autocomplete = new google.maps.places.Autocomplete(destinationModalEl.destinationInput, {
        bounds: {
          north: origin.lat + BIAS_BOUND_DISTANCE,
          south: origin.lat - BIAS_BOUND_DISTANCE,
          east: origin.lng + BIAS_BOUND_DISTANCE,
          west: origin.lng - BIAS_BOUND_DISTANCE
        },
        fields: ['place_id', 'geometry', 'name']
      });
      var destinationFormReset = function () {
        destinationModalEl.destinationInput.classList.remove('error');
        destinationModalEl.errorMessage.textContent = '';
        destinationModalEl.form.reset();
        destinationToAdd = null;
      };
      autocomplete.addListener('place_changed', function () {
        var place = autocomplete.getPlace();
        if (!place.geometry || !place.geometry.location) return;
        destinationToAdd = place;
        destinationModalEl.destinationInput.classList.remove('error');
        destinationModalEl.errorMessage.textContent = '';
      });
      destinationModalEl.addButton.addEventListener('click', function () {
        if (!validateDestinationInput(destinationToAdd)) return;
        addDestinationToList(destinationToAdd, destinationModalEl.getTravelModeInput().value);
        destinationFormReset();
        hideModal();
      });
      destinationModalEl.editButton.addEventListener('click', function () {
        var destination = Object.assign({}, destinations[activeDestinationIndex]);
        var selectedTravelMode = destinationModalEl.getTravelModeInput().value;
        var isSameDestination = destination.name === destinationModalEl.destinationInput.value;
        var isSameTravelMode = destination.travelModeEnum === selectedTravelMode;
        if (isSameDestination && isSameTravelMode) {
          hideModal();
          return;
        }
        if (!isSameDestination) {
          if (!validateDestinationInput(destinationToAdd)) return;
          destination.name = destinationToAdd.name;
          destination.place_id = destinationToAdd.place_id;
          destination.url = generateMapsUrl(destinationToAdd, selectedTravelMode);
        }
        if (!isSameTravelMode) {
          destination.travelModeEnum = selectedTravelMode;
          destination.url = generateMapsUrl(destination, selectedTravelMode);
        }
        destinationFormReset();
        getDirections(destination).then(function (response) {
          if (!response) return;
          removeDirectionsFromMapView(destination);
          destinations[activeDestinationIndex] = destination;
          getCommutesInfo(response, destination);
          assignMapObjectListeners(destination, activeDestinationIndex);
          updateCommutesPanel(destination, activeDestinationIndex, DestinationOperation.EDIT);
          handleRouteClick(destination, activeDestinationIndex);
        });
        hideModal();
      });
      destinationModalEl.cancelButton.addEventListener('click', function () {
        destinationFormReset();
        hideModal();
      });
      destinationModalEl.deleteButton.addEventListener('click', function () {
        removeDirectionsFromMapView(destinations[activeDestinationIndex]);
        updateCommutesPanel(destinations[activeDestinationIndex], activeDestinationIndex, DestinationOperation.DELETE);
        activeDestinationIndex = undefined;
        destinationFormReset();
        var elToFocus;
        if (destinations.length) {
          var lastIndex = destinations.length - 1;
          handleRouteClick(destinations[lastIndex], lastIndex);
          elToFocus = destinationPanelEl.getActiveDestination();
        } else {
          elToFocus = commutesEl.initialStatePanel.querySelector('.add-button');
        }
        hideModal(elToFocus);
      });
      commutesEl.modal.addEventListener('mousedown', function (event) {
        if (event.target === commutesEl.modal) {
          destinationFormReset();
          hideModal();
        }
      });
    }

    function validateDestinationInput(destinationToAdd) {
      var errorMessage = '';
      var isValidInput = false;
      if (!destinationToAdd) {
        errorMessage = 'No details available for destination input';
      } else if (destinations.length >= MAX_NUM_DESTINATIONS) {
        errorMessage = 'Cannot add more than ' + MAX_NUM_DESTINATIONS + ' destinations';
      } else if (destinations.find(function (destination) { return destination.place_id === destinationToAdd.place_id; })) {
        errorMessage = 'Destination is already added';
      } else {
        isValidInput = true;
      }
      if (!isValidInput) {
        destinationModalEl.errorMessage.textContent = errorMessage;
        destinationModalEl.destinationInput.classList.add('error');
      }
      return isValidInput;
    }

    function removeDirectionsFromMapView(destination) {
      if (!destination.polylines) return;
      destination.polylines.innerStroke.setMap(null);
      destination.polylines.outerStroke.setMap(null);
      destination.marker.setMap(null);
    }

    function generateDestinationTemplate(destination) {
      var icon = '#commutes-' + String(destination.travelModeEnum).toLowerCase() + '-icon-' + iconSuffix;
      return (
        '<div class="destination" tabindex="0" role="button">' +
          '<div class="destination-content">' +
            '<div class="metadata">' +
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><use href="' + icon + '"/></svg>' +
              escapeHtml(destination.distance || '') +
              '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><use href="#commutes-arrow-icon-' + iconSuffix + '"/></svg>' +
              '<span class="location-marker">' + escapeHtml(destination.label) + '</span>' +
            '</div>' +
            '<div class="address">To <abbr title="' + escapeHtml(destination.name) + '">' + escapeHtml(destination.name) + '</abbr></div>' +
            '<div class="destination-eta">' + escapeHtml(destination.duration || '') + '</div>' +
          '</div>' +
        '</div>' +
        '<div class="destination-controls">' +
          '<a class="directions-button" href="' + escapeHtml(destination.url) + '" target="_blank" rel="noopener noreferrer" aria-label="Directions">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><use href="#commutes-directions-icon-' + iconSuffix + '"/></svg>' +
          '</a>' +
          '<button type="button" class="edit-button" aria-label="Edit">' +
            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><use href="#commutes-edit-icon-' + iconSuffix + '"/></svg> Edit' +
          '</button>' +
        '</div>'
      );
    }

    function buildDestinationCardTemplate(destination, destinationIdx, destinationOperation) {
      var editButtonEl;
      if (destinationOperation === DestinationOperation.ADD) {
        destinationPanelEl.list.insertAdjacentHTML('beforeend', '<div class="destination-container">' + generateDestinationTemplate(destination) + '</div>');
        var destinationContainerEl = destinationPanelEl.list.lastElementChild;
        destinationContainerEl.addEventListener('click', function () {
          handleRouteClick(destination, destinationIdx);
        });
        editButtonEl = destinationContainerEl.querySelector('.edit-button');
        destinationPanelEl.container.scrollLeft = destinationPanelEl.container.scrollWidth;
      } else if (destinationOperation === DestinationOperation.EDIT) {
        var activeDestinationContainerEl = destinationPanelEl.getActiveDestination().parentElement;
        activeDestinationContainerEl.innerHTML = generateDestinationTemplate(destination);
        activeDestinationContainerEl.addEventListener('click', function () {
          handleRouteClick(destination, destinationIdx);
        });
        editButtonEl = activeDestinationContainerEl.querySelector('.edit-button');
      }
      if (!editButtonEl) return;
      editButtonEl.addEventListener('click', function (event) {
        event.stopPropagation();
        destinationModalEl.title.textContent = strings.edit_destination || 'Edit destination';
        destinationModalEl.destinationInput.value = destination.name;
        showElement(destinationModalEl.deleteButton, null, 'inline');
        showElement(destinationModalEl.editButton, null, 'inline');
        hideElement(destinationModalEl.addButton);
        showModal();
        setTravelModeChecked(destination.travelModeEnum);
      });
    }

    function updateCommutesPanel(destination, destinationIdx, destinationOperation) {
      if (destinationOperation === DestinationOperation.ADD) {
        hideElement(commutesEl.initialStatePanel);
        showElement(commutesEl.destinationPanel, null, 'block');
        buildDestinationCardTemplate(destination, destinationIdx, destinationOperation);
      } else if (destinationOperation === DestinationOperation.EDIT) {
        buildDestinationCardTemplate(destination, destinationIdx, destinationOperation);
      } else if (destinationOperation === DestinationOperation.DELETE) {
        destinations.splice(destinationIdx, 1);
        destinationPanelEl.list.innerHTML = '';
        destinations.forEach(function (item, index) {
          buildDestinationCardTemplate(item, index, DestinationOperation.ADD);
          assignMapObjectListeners(item, index);
        });
      }
      if (!destinations.length) {
        showElement(commutesEl.initialStatePanel, null, 'flex');
        hideElement(commutesEl.destinationPanel);
        activeDestinationIndex = undefined;
        return;
      }
      destinationPanelEl.container.addEventListener('scroll', handlePanelScroll);
      destinationPanelEl.container.dispatchEvent(new Event('scroll'));
    }

    function addDestinationToList(destinationToAdd, travelModeEnum) {
      var destinationConfig = createDestinationConfig(destinationToAdd, travelModeEnum);
      var newDestinationIndex = destinations.length;
      getDirections(destinationConfig).then(function (response) {
        if (!response) return;
        destinations.push(destinationConfig);
        getCommutesInfo(response, destinationConfig);
        assignMapObjectListeners(destinationConfig, newDestinationIndex);
        updateCommutesPanel(destinationConfig, newDestinationIndex, DestinationOperation.ADD);
        handleRouteClick(destinationConfig, newDestinationIndex);
      });
    }

    function getNextMarkerLabel() {
      var markerLabels = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      var label = markerLabels[markerIndex];
      markerIndex = (markerIndex + 1) % markerLabels.length;
      return label;
    }

    function createDestinationConfig(destinationToAdd, travelModeEnum, label) {
      return {
        name: destinationToAdd.name,
        place_id: destinationToAdd.place_id,
        label: label || getNextMarkerLabel(),
        travelModeEnum: travelModeEnum,
        url: generateMapsUrl(destinationToAdd, travelModeEnum)
      };
    }

    function getDirections(destination) {
      return new google.maps.DirectionsService().route({
        origin: origin,
        destination: { placeId: destination.place_id },
        travelMode: destination.travelModeEnum,
        unitSystem: google.maps.UnitSystem.METRIC
      });
    }

    function getCommutesInfo(directionResponse, destination) {
      if (!directionResponse) return;
      var path = directionResponse.routes[0].overview_path;
      var bounds = directionResponse.routes[0].bounds;
      var directionLeg = directionResponse.routes[0].legs[0];
      var innerStroke = new google.maps.Polyline({
        path: path,
        strokeColor: STROKE_COLORS.inactive.innerStroke,
        strokeOpacity: 1,
        strokeWeight: 3,
        zIndex: 10
      });
      var outerStroke = new google.maps.Polyline({
        path: path,
        strokeColor: STROKE_COLORS.inactive.outerStroke,
        strokeOpacity: 1,
        strokeWeight: 6,
        zIndex: 1
      });
      var marker = createMarker(directionLeg.end_location, destination.label);
      innerStroke.setMap(commutesMap);
      outerStroke.setMap(commutesMap);
      destination.distance = directionLeg.distance.text;
      destination.duration = convertDurationValueAsString(directionLeg.duration.value);
      destination.marker = marker;
      destination.polylines = { innerStroke: innerStroke, outerStroke: outerStroke };
      destination.bounds = bounds;
    }

    function assignMapObjectListeners(destination, destinationIdx) {
      google.maps.event.clearListeners(destination.marker, 'click');
      google.maps.event.addListener(destination.marker, 'click', function () {
        handleRouteClick(destination, destinationIdx);
      });
      Object.keys(destination.polylines).forEach(function (key) {
        google.maps.event.clearListeners(destination.polylines[key], 'click');
        google.maps.event.addListener(destination.polylines[key], 'click', function () {
          handleRouteClick(destination, destinationIdx);
        });
      });
    }

    function generateMapsUrl(destination, travelModeEnum) {
      return 'https://www.google.com/maps/dir/?api=1' +
        '&origin=' + origin.lat + ',' + origin.lng +
        '&destination=' + encodeURIComponent(destination.name) +
        '&destination_place_id=' + destination.place_id +
        '&travelmode=' + String(travelModeEnum).toLowerCase();
    }

    function handleRouteClick(destination, destinationIdx) {
      if (activeDestinationIndex !== undefined && destinations[activeDestinationIndex] && destinations[activeDestinationIndex].polylines) {
        destinations[activeDestinationIndex].polylines.innerStroke.setOptions({ strokeColor: STROKE_COLORS.inactive.innerStroke, zIndex: 2 });
        destinations[activeDestinationIndex].polylines.outerStroke.setOptions({ strokeColor: STROKE_COLORS.inactive.outerStroke, zIndex: 1 });
        destinations[activeDestinationIndex].marker.setIcon(destinationMarkerIcon);
        destinations[activeDestinationIndex].marker.label.color = MARKER_ICON_COLORS.inactive.label;
        var activeDestinationEl = destinationPanelEl.getActiveDestination();
        if (activeDestinationEl) activeDestinationEl.classList.remove('active');
      }
      activeDestinationIndex = destinationIdx;
      setTravelModeLayer(destination.travelModeEnum);
      var newDestinationEl = destinationPanelEl.list.querySelectorAll('.destination')[destinationIdx];
      if (newDestinationEl) {
        newDestinationEl.classList.add('active');
        newDestinationEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
      destination.polylines.innerStroke.setOptions({ strokeColor: STROKE_COLORS.active.innerStroke, zIndex: 101 });
      destination.polylines.outerStroke.setOptions({ strokeColor: STROKE_COLORS.active.outerStroke, zIndex: 99 });
      destination.marker.setIcon(originMarkerIcon);
      destination.marker.label.color = '#ffffff';
      commutesMap.fitBounds(destination.bounds);
    }

    function createMarker(location, label) {
      var isOrigin = label === undefined;
      return new google.maps.Marker({
        position: location,
        map: commutesMap,
        label: {
          text: isOrigin ? '●' : label,
          fontFamily: 'inherit',
          color: isOrigin ? MARKER_ICON_COLORS.active.label : MARKER_ICON_COLORS.inactive.label,
          fontSize: isOrigin ? '20px' : '16px'
        },
        icon: isOrigin ? originMarkerIcon : destinationMarkerIcon
      });
    }

    function setTravelModeLayer(travelModeEnum) {
      if (travelModeEnum === TravelMode.BICYCLING) {
        publicTransitLayer.setMap(null);
        bikeLayer.setMap(commutesMap);
      } else if (travelModeEnum === TravelMode.TRANSIT) {
        bikeLayer.setMap(null);
        publicTransitLayer.setMap(commutesMap);
      } else {
        publicTransitLayer.setMap(null);
        bikeLayer.setMap(null);
      }
    }

    function convertDurationValueAsString(durationValue) {
      if (!durationValue) return '';
      if (durationValue < MIN_IN_SECONDS) return '<1 min';
      if (durationValue > HOUR_IN_SECONDS * 10) return '10+ hours';
      var hours = Math.floor(durationValue / HOUR_IN_SECONDS);
      var minutes = Math.floor((durationValue % HOUR_IN_SECONDS) / 60);
      var hoursString = hours > 0 ? hours + ' h' : '';
      var minutesString = minutes > 0 ? minutes + ' min' : '';
      return hoursString + (hoursString && minutesString ? ' ' : '') + minutesString;
    }

    function showModal() {
      lastActiveEl = document.activeElement;
      showElement(commutesEl.modal, destinationModalEl.destinationInput, 'flex');
    }

    function hideModal(focusEl) {
      hideElement(commutesEl.modal, focusEl || lastActiveEl);
    }

    function handleScrollButtonClick(event) {
      var direction = Number(event.target.dataset.direction);
      var card = destinationPanelEl.list.firstElementChild;
      if (!card) return;
      destinationPanelEl.container.scrollBy({ left: direction * card.offsetWidth * 1.25, behavior: 'smooth' });
    }

    function handlePanelScroll() {
      var position = destinationPanelEl.container.scrollLeft;
      var scrollWidth = destinationPanelEl.container.scrollWidth;
      var width = destinationPanelEl.container.offsetWidth;
      var canScroll = scrollWidth > width + 1;
      destinationPanelEl.scrollLeftButton.classList.toggle('hide', !canScroll || position <= 0);
      destinationPanelEl.scrollRightButton.classList.toggle('hide', !canScroll || Math.ceil(position + width) >= scrollWidth);
    }
  }

  function initTabs(root, widgets) {
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-maps-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-maps-panel]'));
    tabs.forEach(function (tab) {
      tab.addEventListener('click', function () {
        var target = tab.getAttribute('data-maps-tab');
        tabs.forEach(function (item) {
          var selected = item === tab;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          var match = panel.getAttribute('data-maps-panel') === target;
          if (match) panel.removeAttribute('hidden');
          else panel.setAttribute('hidden', '');
        });
        window.setTimeout(function () {
          if (target === 'nearby' && widgets.neighborhood && widgets.neighborhood.resize) widgets.neighborhood.resize();
          if (target === 'commute') {
            if (!widgets.commutes && widgets.initCommutes) widgets.initCommutes();
            else if (widgets.commutes && widgets.commutes.resize) widgets.commutes.resize();
          }
        }, 50);
      });
    });
  }

  function initRoot(root) {
    if (root.dataset.storeMapsReady === '1') return;
    var configNode = root.querySelector('[data-store-maps-config]');
    if (!configNode) return;
    var config;
    try {
      config = JSON.parse(configNode.textContent);
    } catch (error) {
      return;
    }
    var center = parseLatLng(config.center);
    if (!center) return;
    root.dataset.storeMapsReady = '1';

    loadGoogleMaps(config.apiKey).then(function () {
      return fillDistanceCards(root, center, config.strings || {});
    }).then(function () {
      var ndRoot = root.querySelector('.neighborhood-discovery');
      var widgets = {};
      if (ndRoot) {
        widgets.neighborhood = new NeighborhoodDiscovery(ndRoot, {
          mapRadius: Number(config.mapRadius) || 2000,
          pois: parsePlaceIds(config.placeIds).map(function (placeId) { return { placeId: placeId }; }),
          mapOptions: {
            center: center,
            fullscreenControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            zoom: 15,
            zoomControl: true,
            maxZoom: 20
          },
          strings: config.strings || {}
        });
      }

      widgets.initCommutes = function () {
        if (widgets.commutes) return;
        var commuteRoot = root.querySelector('.commutes');
        if (!commuteRoot) return;
        var attrib = document.createElement('div');
        attrib.hidden = true;
        commuteRoot.appendChild(attrib);
        var placesService = new google.maps.places.PlacesService(attrib);
        var cards = Array.prototype.slice.call(root.querySelectorAll('[data-distance-card]'));
        Promise.all(cards.map(function (card) {
          return findPlaceFromQuery(placesService, card.getAttribute('data-query'));
        })).then(function (places) {
          var initialDestinations = places.filter(Boolean).map(function (place) {
            return { placeId: place.place_id, travelMode: 'WALKING' };
          });
          widgets.commutes = new Commutes(commuteRoot, {
            defaultTravelMode: 'WALKING',
            iconSuffix: config.iconSuffix || '',
            strings: config.strings || {},
            mapOptions: {
              center: center,
              fullscreenControl: true,
              mapTypeControl: false,
              streetViewControl: false,
              zoom: 14,
              zoomControl: true,
              maxZoom: 20
            },
            initialDestinations: initialDestinations
          });
          window.setTimeout(function () {
            if (widgets.commutes.resize) widgets.commutes.resize();
          }, 80);
        });
      };

      initTabs(root, widgets);
    }).catch(function () {
      root.dataset.storeMapsReady = '0';
    });
  }

  function boot() {
    var roots = document.querySelectorAll('[data-store-maps]');
    if (!roots.length) return;
    var observer = 'IntersectionObserver' in window
      ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          initRoot(entry.target);
        });
      }, { rootMargin: '200px' })
      : null;
    roots.forEach(function (root) {
      if (observer) observer.observe(root);
      else initRoot(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
